// ----------------------------------------------------------------------
// <copyright file="Izenda.PrintablePage.jsx" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
// </copyright>
// ----------------------------------------------------------------------

import React, { Component } from 'react'
import PropTypes from 'prop-types';
import connect from 'share/enhancers/Izenda.Connect'
import { getOr, merge, reduce } from 'lodash/fp';
import PrintConstants from 'logic/data/Izenda.PrintConstants'
import GridLayout from 'share/components/controls/Izenda.GridLayout'
import classnames from 'classnames'
import ReportHeader from 'modules/ReportDesigner/Fields/Izenda.ReportHeader'
import ReportFooter from 'modules/ReportDesigner/Fields/Izenda.ReportFooter'
import ReportBodyViewer from 'share/components/views/Izenda.ReportBodyViewer'
import PrintingUtils from 'share/utils/Izenda.PrintingUtils'
import ScrollBox from 'share/components/controls/Izenda.ScrollBox'
import ExportConstants from 'logic/data/Izenda.ExportConstants';
import ReportDesignerFormatConstants from 'logic/data/Izenda.ReportDesignerFormatsConstants';

@connect({ toClass: true })
export default class PrintablePage extends Component {
  static propTypes = {
    pageSizeKey: PropTypes.string // A0, A1, A2,...
  };

  static defaultProps = {
    viewMode: true
  };

  setDom = (dom: any) => {
    this.containerRef = dom;
  };

  static getSizeUi = value => value < PrintConstants.MIN_SIZE_UI_SETTING ? PrintConstants.MIN_SIZE_UI_SETTING : value;

  get pageMargins() {
    const { top, right, bottom, left } = this.props;
    return {
      top: PrintablePage.getSizeUi(top),
      right: PrintablePage.getSizeUi(right),
      bottom: PrintablePage.getSizeUi(bottom),
      left: PrintablePage.getSizeUi(left)
    };
  };

  get pageSizeKey() {
    const { props } = this;
    return getOr(null, 'pageSizeKey', props) || PrintConstants.DEFAULT_PAGE_SIZE_KEY;
  }

  getFormatItemsHeight(formatItems) {
    return reduce(
      (acc, formatItem) => Math.max(formatItem.positionY + formatItem.height, acc),
      0,
      formatItems);
  }

  getHeaderAndFooterRealSize() {
    const {
      props,
      props: {
        store: {
          isVisibleHeaderFooter
        },
        exportFormatSetting: {
          scaleX
        }
      }
    } = this;
    if (!isVisibleHeaderFooter) {
      return {
        headerHeight: 0,
        footerHeight: 0
      }
    }
    const ROW_HEIGHT = ReportDesignerFormatConstants.ROW_HEIGHT;
    const headerFormatItems = getOr([], 'store.reportHeader.formatItems', props);
    const headerGridHeight = this.getFormatItemsHeight(headerFormatItems);
    const headerHeightScaled = scaleX * ROW_HEIGHT * headerGridHeight;

    const footerFormatItems = getOr([], 'store.reportFooter.formatItems', props);
    const footerGridHeight = this.getFormatItemsHeight(footerFormatItems);
    const footerHeightScaled = scaleX * ROW_HEIGHT * footerGridHeight;

    return {
      headerHeight: headerHeightScaled,
      footerHeight: footerHeightScaled
    };
  }

  getHeaderAndFooterRealInches(clientHeight) {
    const { props, pageSizeKey } = this;
    const { orientation } = props;
    const pageSizeInches = PrintConstants.getPageSize(pageSizeKey, orientation);
    const pxPerInchInHeight = clientHeight / pageSizeInches.heightInInches;
    const headerAndFooterRealHeights = this.getHeaderAndFooterRealSize();
    const header = headerAndFooterRealHeights.headerHeight / pxPerInchInHeight;
    const footer = headerAndFooterRealHeights.footerHeight / pxPerInchInHeight;
    return {
      header, footer
    }
  }

  /**
   * Render content
   */
  renderContent = () => {
    const clientHeight = getOr(null, 'containerRef.clientHeight', this);
    if (!clientHeight)
      return null;

    const { props, pageSizeKey, pageMargins } = this;
    const {
      vertically,
      horizontally,
      orientation,
      exportFormatSetting,
      viewMode,
      partsToRender,
      pageIndex,
      pageBreakAfterReportPart
    } = props;
    const pageSizeInches = PrintConstants.getPageSize(pageSizeKey, orientation);

    const { scaleX, scaleWidth, scaleHeight, margins } = exportFormatSetting;

    const isCustomMargin = margins === ExportConstants.MARGIN_VALUE.CUSTOM;

    // Calc header and footer inches
    let { header, footer } = props;
    const realHeaderAndFooter = this.getHeaderAndFooterRealInches(clientHeight);
    if (!isCustomMargin) {
      header = realHeaderAndFooter.header;
      footer = realHeaderAndFooter.footer;
    } else {
      header = Math.max(header, realHeaderAndFooter.header);
      footer = Math.max(footer, realHeaderAndFooter.footer);
    }
    // Limit max values by 2/3 of whole page height
    header = Math.min(header, pageSizeInches.heightInInches * 2 / 3);
    footer = Math.min(footer, pageSizeInches.heightInInches * 2 / 3);

    // Calc content size
    const contentSizeInInches = PrintConstants.getBodyContentSizeInInches(
      pageSizeKey,
      pageMargins,
      header,
      footer,
      orientation);

    const contentContainerHeightInInches = contentSizeInInches.heightOfContentInInches + header + footer;
    const headerHeightInPercent = Math.round(header * 100 / contentContainerHeightInInches);
    const footerHeightInPercent = Math.round(footer * 100 / contentContainerHeightInInches);
    const contentHeightInPercent = 100 - (headerHeightInPercent + footerHeightInPercent);

    const contentContainerClassName = classnames('u_noMarginAll u_noPaddingAll u_fullSize');
    const contentClassName = classnames('u_tableCell', {
      // 'u_alignTop': !vertically,
      // 'u_alignMiddle': vertically
    });
    const headerContainerClassName = classnames('u_noMarginAll u_noPaddingAll u_fullSize u_table');
    const headerClassName = classnames('u_tableCell', {
      // 'u_alignTop': !vertically,
      // 'u_alignMiddle': vertically
    });
    const footerContainerClassName = classnames('u_noMarginAll u_noPaddingAll u_fullSize u_table');
    const footerClassName = classnames('u_tableCell', {
      'u_alignTop': !vertically,
      'u_alignMiddle': vertically
    });

    const scaleStyleSimple = PrintingUtils.buildScaleStyleSimple(scaleX);

    // header styles
    const scaleStyleHeader = {
      height: PrintConstants.formatCSSValueInPercent(headerHeightInPercent)
    };
    const scaleStyleHeaderSub = merge({
      height: PrintConstants.formatCSSValueInPercent(headerHeightInPercent * scaleX),
      width: PrintConstants.formatCSSValueInPercent(100 / scaleX)
    }, scaleStyleSimple);

    const scaleStyleFooter = {
      height: PrintConstants.formatCSSValueInPercent(footerHeightInPercent)
    };
    const scaleStyleFooterSub = merge({
      height: PrintConstants.formatCSSValueInPercent(footerHeightInPercent * scaleX),
      width: PrintConstants.formatCSSValueInPercent(100 / scaleX)
    }, scaleStyleSimple);

    let contentScaleStyle = PrintingUtils.buildScaleStyle(scaleX, null, {
      partsToRender: partsToRender,
      width: scaleWidth,
      height: scaleHeight,
      vertically: vertically,
      horizontally: horizontally,
      isPageBreak: pageBreakAfterReportPart
    });

    const bbox = PrintingUtils.getBbox(partsToRender, false);
    const gridSizeScaledPx = PrintConstants.isPortrait(orientation)
      ? scaleWidth / 12
      : scaleWidth / 12 / Math.sqrt(2);
    const gridStyle = {
      display: 'grid',
      gridTemplateColumns: '1fr'
    };
    const gridItemStyle = {
      alignSelf: vertically ? 'center' : 'start',
      justifySelf: horizontally ? 'center' : 'start',
      width: (bbox.x2 * gridSizeScaledPx) + 'px',
      height: (bbox.y2 * gridSizeScaledPx + 5) + 'px',
      overflow: 'hidden'
    };
    contentScaleStyle = merge({
      width: (100 / scaleX) + '%',
      height: (100 / scaleX) + '%'
    }, contentScaleStyle);

    return (

      <GridLayout className='u_fullSize'>
        {/*header*/}
        <GridLayout.Row
          className='u_noMarginAll u_noPaddingAll u_overflowHidden'
          style={scaleStyleHeader}>
          <GridLayout.Col
            className={headerContainerClassName}
            style={scaleStyleHeaderSub}>
            <ReportHeader
              className={headerClassName}
              setting
              viewMode
              horizontalAlign={horizontally}
              replaceSystemVariable={PrintingUtils.replaceSystemVariable}
              store={props.store}/>
          </GridLayout.Col>
        </GridLayout.Row>

        <GridLayout.Row
          className='body-content u_noMarginAll u_noPaddingAll u_fullSize'
          style={{ height: PrintConstants.formatCSSValueInPercent(contentHeightInPercent) }}>
          <GridLayout.Col className={contentContainerClassName}>
            <ScrollBox className="u_fullSize" fullWidth>
              <div className="u_fullSize" style={gridStyle}>
                <div style={gridItemStyle}>
                  <div className="u_fullSize" style={contentScaleStyle}>
                    <ReportBodyViewer
                      className={contentClassName}
                      id={props.store.reportId}
                      exportFormatSetting={props.exportFormatSetting}
                      reportDesignerStore={props.reportDesignerStore}
                      printingMode
                      setting
                      skipLoad
                      pageIndex={pageIndex}
                      viewMode={viewMode}
                      partsToRender={partsToRender}/>
                  </div>
                </div>
              </div>
            </ScrollBox>
          </GridLayout.Col>
        </GridLayout.Row>

        {/*footer*/}
        <GridLayout.Row
          className='footer-content u_noMarginAll u_noPaddingAll u_overflowHidden'
          style={scaleStyleFooter}>
          <GridLayout.Col
            style={scaleStyleHeaderSub}
            className={footerContainerClassName}>
            <ReportFooter
              className={footerClassName}
              setting
              viewMode
              horizontalAlign={horizontally}
              store={props.store}/>
          </GridLayout.Col>
        </GridLayout.Row>
      </GridLayout>
    );
  };

  render() {
    const { props, pageSizeKey, pageMargins } = this;
    const { orientation } = props;

    // Calculate margin styles:
    const marginPercents = PrintingUtils.getPageMarginsInPercent(pageSizeKey, orientation, pageMargins);
    const columnStyles = [{
      width: PrintConstants.formatCSSValueInPercent(marginPercents.horizontal.left)
    }, {
      width: PrintConstants.formatCSSValueInPercent(marginPercents.line)
    }, {
      width: PrintConstants.formatCSSValueInPercent(marginPercents.horizontal.center)
    }, {
      width: PrintConstants.formatCSSValueInPercent(marginPercents.line)
    }, {
      width: PrintConstants.formatCSSValueInPercent(marginPercents.horizontal.right)
    }];
    const rowStyles = [{
      height: PrintConstants.formatCSSValueInPercent(marginPercents.vertical.top)
    }, {
      height: PrintConstants.formatCSSValueInPercent(marginPercents.line)
    }, {
      height: PrintConstants.formatCSSValueInPercent(marginPercents.vertical.center)
    }, {
      height: PrintConstants.formatCSSValueInPercent(marginPercents.line)
    }, {
      height: PrintConstants.formatCSSValueInPercent(marginPercents.vertical.bottom)
    }];

    return (
      <div id="izenda-printable-page" className="u_fullSize" ref={this.setDom}>
        <GridLayout className="izenda-PrintablePage u_fullSize">
          {/* row 0 */}
          <GridLayout.Row
            className="u_noMarginAll u_noPaddingAll"
            style={{ height: rowStyles[0].height }}>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[0].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="8"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[2].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[4].width }}/>
          </GridLayout.Row>
          {/* row 1 */}
          <GridLayout.Row
            className="non-height u_noMarginAll u_noPaddingAll u_fullWidth u_table">
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[0].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="8"
              className="y-line u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[2].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[4].width }}/>
          </GridLayout.Row>
          {/* row 2 */}
          <GridLayout.Row
            className="u_noMarginAll u_noPaddingAll"
            style={{ height: rowStyles[2].height }}>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[0].width }}/>
            <GridLayout.Col
              cols="1"
              className="x-line non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="8"
              className="u_noMarginAll u_noPaddingAll u_fullSize"
              style={{ width: columnStyles[2].width }}>
              {/* content here */}
              {this.renderContent()}
            </GridLayout.Col>
            <GridLayout.Col
              cols="1"
              className="x-line non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[4].width }}/>
          </GridLayout.Row>
          {/* row 3 */}
          <GridLayout.Row
            className="non-height u_noMarginAll u_noPaddingAll u_fullWidth u_table">
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[0].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="8"
              className="y-line u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[2].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[4].width }}/>
          </GridLayout.Row>
          {/* row 4 */}
          <GridLayout.Row
            className="u_noMarginAll u_noPaddingAll"
            style={{ height: rowStyles[4].height }}>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[0].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="8"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[2].width }}/>
            <GridLayout.Col
              cols="1"
              className="non-width u_noMarginAll u_noPaddingAll u_fullHeight"/>
            <GridLayout.Col
              cols="1"
              className="u_noMarginAll u_noPaddingAll"
              style={{ width: columnStyles[4].width }}/>
          </GridLayout.Row>
        </GridLayout>
      </div>
    );
  }
}




// WEBPACK FOOTER //
// ./ui/share/components/controls/Izenda.PrintablePage.js